/**
 * @typedef {import('../types/Assignments').Assignment} Assignment
 * @typedef {import('../types/Assignments').Assignments} Assignments
 * @typedef {import('../types/ScheduleValue').ScheduleValue} ScheduleValue
 */

import ASSIGNMENT_TYPES from '../types/AssignmentTypes';
import { SEQUENCE } from '../types/SequenceType';

class AssignmentSelectionHelper {
  /**
   * Initializes the AssignmentSelectionHelper with all assignments, selected assignments, and an optional edit sequence ID.
   * @param {Assignments} allAssignments - All possible assignments.
   * @param {Object.<string, Assignment>} selectedAssignments - The currently selected assignments, keyed by assignment ID.
   * @param {number} [editSequenceId] - Optional ID of the sequence being edited, used for pre-selecting assignments.
   * @param {ScheduleValue} [schedule] - Optional schedule, used for assignment selection logic.
   */
  constructor(allAssignments, selectedAssignments, editSequenceId, schedule) {
    this.allAssignments = allAssignments;
    this.selectedAssignments = selectedAssignments;
    this.editSequenceId = editSequenceId;
    this.schedule = schedule;
  }

  /**
   * Checks if an assignment is selected.
   * @param {Assignment} assignment - The assignment to check.
   * @returns {boolean} True if selected, false otherwise.
   */
  isAssignmentSelected(assignment) {
    return this.selectedAssignments[assignment.id] !== undefined;
  }

  /**
   * Checks if an assignment is already assigned to the sequence being edited.
   * This effectively renders the assignment read-only in the UI, as it cannot be unselected.
   * @param {Assignment} assignment - The assignment to check.
   * @returns {boolean} True if already assigned, false otherwise.
   */
  isAssignmentAlreadyAssigned(assignment) {
    if (this.schedule) {
      // if new scheduling, no device should be considered assigned
      return false;
    }

    return (
      assignment.content_type === SEQUENCE && Number(assignment.content_id) === this.editSequenceId
    );
  }

  /**
   * Checks if a location and device assignment pair is connected.
   * @param {Assignment} device - The device assignment.
   * @param {Assignment} location - The location assignment.
   */
  #isDeviceInLocation(device, location) {
    const locationPath = `${location.parent}${location.name}/`;

    if (device.parent === locationPath) {
      return true;
    }

    // location.parent* matches device.parent
    // add trailing slash if missing to avoid partial matching
    return device.parent.startsWith(locationPath);
  }

  /**
   * Checks if an assignment is a device assignment.
   * @param {Assignment} assignment - The assignment to check.
   * @returns {boolean} True if device assignment, false otherwise.
   */
  isAssignmentDevice(assignment) {
    return assignment.type === ASSIGNMENT_TYPES.device;
  }

  /**
   * Gets the locations for a device assignment.
   * @param {Assignment} device - The device assignment.
   * @returns {Assignment[]} The locations for the device.
   */
  #getAllDeviceLocations(device) {
    const allAssignment = this.allAssignments.assignments;

    return allAssignment
      .filter(a => this.isAssignmentLocation(a))
      .filter(a => this.#isDeviceInLocation(device, a));
  }

  /**
   * Gets the devices for a location assignment.
   * @param {Assignment} location - The location assignment.
   * @returns {Assignment[]} The devices for the location.
   */
  #getAllLocationDevices(location) {
    const allAssignment = this.allAssignments.assignments;

    return allAssignment
      .filter(a => this.isAssignmentDevice(a))
      .filter(a => this.#isDeviceInLocation(a, location));
  }

  /**
   * Checks if all devices in a location are selected.
   * @param {Assignment} location - The location assignment.
   * @returns {boolean} True if all devices in the location are selected, false otherwise.
   */
  #isAllDevicesInLocationSelected(location) {
    return this.#getAllLocationDevices(location).every(deviceAssignment =>
      this.isAssignmentSelected(deviceAssignment),
    );
  }

  /**
   * Selects an assignment.
   * @param {Assignment} assignment - The assignment to select.
   */
  #selectAssignment(assignment) {
    const selectedAssignments = this.selectedAssignments;

    // add new selection
    selectedAssignments[assignment.id] = assignment;

    if (this.isAssignmentDevice(assignment)) {
      // check if all devices in locations are selected then select location as well
      const locations = this.#getAllDeviceLocations(assignment);

      locations.forEach(locationAssignment => {
        if (this.#isAllDevicesInLocationSelected(locationAssignment)) {
          selectedAssignments[locationAssignment.id] = locationAssignment;
        }
      });
    }

    if (this.isAssignmentLocation(assignment)) {
      // select all devices in location
      this.#getAllLocationDevices(assignment).forEach(deviceAssignment => {
        selectedAssignments[deviceAssignment.id] = deviceAssignment;
      });
    }

    this.selectedAssignments = selectedAssignments;
  }

  /**
   * Checks if an assignment is a location assignment.
   * @param {Assignment} assignment - The assignment to check.
   * @returns {boolean} True if location assignment, false otherwise.
   */
  isAssignmentLocation(assignment) {
    return assignment.type === ASSIGNMENT_TYPES.location;
  }

  /**
   * Gets the Set of selected assignment IDs.
   * @returns {string[]} selected IDs.
   */
  get selectedDeviceIds() {
    return Object.values(this.selectedAssignments)
      .filter(a => this.isAssignmentDevice(a))
      .map(a => a.device_id);
  }

  /**
   * Gets the Set of selected device assignments.
   * @returns {Assignment[]} selected device assignments.
   */
  get selectedDeviceAssignments() {
    return Object.values(this.selectedAssignments).filter(a => this.isAssignmentDevice(a));
  }

  /**
   * Gets the Set of selected assignment location IDs.
   * @returns {string[]} selected location IDs.
   */
  get selectedLocationIds() {
    return Object.values(this.selectedAssignments)
      .filter(a => this.isAssignmentLocation(a))
      .map(a => a.location_id);
  }

  /**
   * Selects an assignment.
   * @param {Assignment} assignment - The assignment to select.
   */
  #unSelectAssignment(assignment) {
    const selectedAssignments = this.selectedAssignments;

    // prevent unselecting devices that are already assigned to the sequence being edited
    if (this.isAssignmentAlreadyAssigned(assignment)) {
      return;
    }

    // remove selection
    delete selectedAssignments[assignment.id];

    if (this.isAssignmentDevice(assignment)) {
      // unselect location if exists
      const locations = this.#getAllDeviceLocations(assignment);

      locations.forEach(locationAssignment => {
        if (this.isAssignmentSelected(locationAssignment)) {
          delete selectedAssignments[locationAssignment.id];
        }
      });
    }

    if (this.isAssignmentLocation(assignment)) {
      // unselect all devices in location
      this.#getAllLocationDevices(assignment).forEach(deviceAssignment => {
        if (
          this.isAssignmentSelected(deviceAssignment) &&
          // prevent unselecting devices that are already assigned to the sequence being edited
          !this.isAssignmentAlreadyAssigned(deviceAssignment)
        ) {
          delete selectedAssignments[deviceAssignment.id];
        }
      });
    }

    this.selectedAssignments = selectedAssignments;
  }

  /**
   * Toggles the selection of an assignment.
   * @param {Assignment} assignment - The assignment to toggle
   */
  toggleAssignment(assignment) {
    if (this.isAssignmentSelected(assignment)) {
      this.#unSelectAssignment(assignment);
    } else {
      this.#selectAssignment(assignment);
    }
  }

  /**
   * Decorates an assignment with additional properties for display purposes, such as content and device counts.
   * @param {Assignment} assignment - The assignment to decorate.
   * @returns {Assignment} The decorated assignment.
   */
  decorateAssignment(assignment) {
    assignment.contentCount = assignment.content_count || 0;
    assignment.deviceCount = assignment.device_count || 0;

    if (this.isAssignmentLocation(assignment)) {
      const locationDevices = this.#getAllLocationDevices(assignment);

      assignment.deviceCount = locationDevices.length;
      assignment.contentCount = locationDevices.reduce((sum, deviceAssignment) => {
        return this.isAssignmentAlreadyAssigned(deviceAssignment) ? sum + 1 : sum;
      }, 0);
    }

    return assignment;
  }
}

export default AssignmentSelectionHelper;
